import { useState, useEffect, useCallback, useRef } from 'react';
import { getThirds, deleteThird } from '../../../usecases/thirds-usecase';
import { EmptyArray } from '../../../domain/empty/model';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { BsPlus, BsPencil, BsTrash, BsEye } from 'react-icons/bs';
import ButtonAction from '../../atoms/buttonAction/view';
import NewThird from '../newThird/view';
import EditThird from '../editThird/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import PersonCardGrid from '../../organisms/PersonCardGrid/view';
import './style.css';

function ThirdsTable({ mode = 'full', filter: initialFilter, forceLoadData, maxHeight, initialThird = {} }) {
    const isSelector = mode === 'selector';
    const [thirds, setThirds] = useState({
        total: 0,
        page: 1,
        page_size: 20,
        data: [],
    });
    const [search, setSearch] = useState(initialFilter || '');
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef(null);


    const fetchThirds = useCallback(async (page = 1, pageSize = 20, query = search) => {
        if (page === 1) {
            setThirds(prev => ({ ...prev, data: [] }));
        }
        setIsLoading(true);
        try {
            const filters = query ? [{ label: 'query', value: query }] : [];
            const res = await getThirds(page, pageSize, filters);
            if (!res?.data?.length) {
                if (page === 1) {
                    setThirds({
                        page,
                        page_size: pageSize,
                        total: res?.total || 0,
                        data: EmptyArray
                    });
                } else {
                    setThirds(prev => ({
                        ...prev,
                        page,
                        total: res?.total || prev.total
                    }));
                }
            } else {
                setThirds(prev => ({
                    ...res,
                    page: res.page || page,
                    page_size: res.page_size || pageSize,
                    total: res.total || 0,
                    data: page === 1 ? res.data : [...prev.data, ...res.data]
                }));
            }
        } catch {
            if (page === 1) {
                setThirds(prev => ({ ...prev, page, page_size: pageSize, data: EmptyArray }));
            }
            dispatchCustomEvent('popup', { message: 'Error al cargar terceros', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchThirds(1, thirds.page_size, initialFilter || '');
    }, [forceLoadData]);

    useEffect(() => {
        const u1 = listener('update-thirds', () => fetchThirds(1, thirds.page_size, search));
        const u2 = listener('btn-thirds-delete', handleDelete);
        return () => { u1(); u2(); };
    }, [thirds.page_size, search, fetchThirds]);

    useEffect(() => {
        const u3 = listener('btn-thirds-add', () => fetchThirds(1, thirds.page_size, search));
        return () => { if (u3) u3(); };
    }, [thirds.page_size, search, fetchThirds]);

    const handleSearch = (val) => {
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchThirds(1, thirds.page_size, val);
        }, 400);
    };

    const handlePageChange = (page) => {
        fetchThirds(page, thirds.page_size, search);
    };

    const handleDelete = async (data) => {
        try {
            await deleteThird(data.id || data);
            fetchThirds(1, thirds.page_size, search);
        } catch (err) {
            dispatchCustomEvent('popup', { message: err?.response?.data?.message || 'Error al eliminar', type: 'error' });
        }
    };

    const handleSelectThird = (person) => {
        dispatchCustomEvent('btn-thirds-add', person);
    };

    const getDocumentLabel = (docId) => {
        const map = { 6: 'NIT', 3: 'CC', 4: 'CE', 5: 'TI', 2: 'Pasaporte' };
        return map[docId] || 'Doc';
    };

    const getTags = (person) => {
        const tags = [];
        if (person.client) tags.push({ label: 'Cliente', color: '#2ecc71' });
        if (person.provider) tags.push({ label: 'Proveedor', color: '#3498db' });
        if (person.other) tags.push({ label: 'Otro', color: '#9ca3af' });
        return tags;
    };

    const mapPerson = (person) => ({
        name: person.name,
        subtitle: `${getDocumentLabel(person.identificationsId)}: ${person.identification || 'N/A'}${person.email ? '  •  ' + person.email : ''}`,
        avatarInitial: (person.name || '?').charAt(0).toUpperCase(),
        status: person.client ? 'success' : person.provider ? 'info' : undefined,
        statusLabel: person.client ? 'Cliente' : person.provider ? 'Proveedor' : undefined,
        tags: getTags(person),
        onClick: isSelector ? () => handleSelectThird(person) : undefined,
    });

    const renderActions = isSelector
        ? (person) => (
            <button
                className="persongrid-select-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    handleSelectThird(person);
                }}
            >
                Seleccionar
            </button>
        )
        : (person) => (
            <>
                <ButtonAction
                    btnKey={`eye-${person.id}`}
                    variant="add"
                    onClick={() => {
                        dispatchCustomEvent(`btn-thirds-add`, person);
                    }}
                />
                <EditThird data={person} />
                <ButtonAction
                    btnKey={`delete-${person.id}`}
                    variant="danger"
                    onClick={() => {
                        dispatchCustomEvent('btn-notification-confirm-delete-third', person);
                    }}
                />

            </>
        );

    return (
        <>
            <ConfirmPopUp
                eventKey="delete-third"
                msg="¿Estás seguro de eliminar este tercero?"
                onAcept={handleDelete}
            />
            <PersonCardGrid
                people={thirds.data}
                variant={isSelector ? 'rows' : 'grid'}
                total={thirds.total}
                page={thirds.page}
                pageSize={thirds.page_size}
                onPageChange={handlePageChange}
                onSearch={handleSearch}
                searchValue={search}
                initialThird={initialThird}
                searchPlaceholder="Buscar por nombre, NIT o email..."
                addLabel={isSelector ? undefined : 'Nuevo Tercero'}
                emptyTitle="No se encontraron terceros"
                emptyDescription="Crea tu primer tercero para empezar a facturar y registrar compras."
                renderPersonCard={mapPerson}
                renderActions={renderActions}
                isLoading={isLoading}
            />

        </>
    );
}

export default ThirdsTable;
